/**
 * Aspect Ratio Split Button Component
 * 
 * Combined button with two actions:
 * 1. Main button: Apply aspect ratio to current project
 * 2. Dropdown arrow: Clone project with new aspect ratio
 */

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faClone, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../context/NotificationContext';
import { getApiUrl } from '../../config/api';

interface AspectRatioSplitButtonProps {
  currentAspectRatio: string;
  isConversionMode: boolean;
  onStartConversion: (targetRatio: string) => void;
  onCancelConversion: () => void;
  disabled?: boolean;
  projectId?: string;
  projectTitle?: string;
  onCloneSuccess?: (newProjectId: string, newProjectTitle: string, targetRatio: string) => void;
}

const AspectRatioSplitButton: React.FC<AspectRatioSplitButtonProps> = ({
  currentAspectRatio,
  isConversionMode,
  onStartConversion,
  onCancelConversion,
  disabled = false,
  projectId,
  projectTitle = 'Project',
  onCloneSuccess
}) => {
  const { t } = useTranslation('videoEditor');
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { showNotification } = useNotification();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Determine target ratio based on current
  const getTargetRatio = () => {
    if (currentAspectRatio === '16:9') return '9:16';
    if (currentAspectRatio === '9:16') return '16:9';
    return null;
  };

  const targetRatio = getTargetRatio();

  // Don't show button if conversion not supported
  if (!targetRatio) return null;

  const getRatioLabel = (ratio: string) => {
    return ratio === '9:16' ? t('videoEditor.aspectRatioSplit.ratioLabels.portrait') : t('videoEditor.aspectRatioSplit.ratioLabels.landscape');
  };

  // Handle main button click (apply to current project)
  const handleMainButtonClick = () => {
    if (isConversionMode) {
      onCancelConversion();
    } else {
      onStartConversion(targetRatio);
    }
  };

  // Handle clone action
  const handleClone = async () => {
    if (!projectId) {
      showNotification({
        type: 'error',
        title: t('videoEditor.aspectRatioSplit.notifications.cloneFailed'),
        message: t('videoEditor.aspectRatioSplit.notifications.projectIdRequired'),
      });
      return;
    }

    try {
      setIsCloning(true);
      console.log(`🔄 [CLONE] Starting clone to ${targetRatio}`);

      const response = await fetch(getApiUrl('/video/clone-aspect-ratio'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          sourceProjectId: projectId,
          targetAspectRatio: targetRatio,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Clone failed with status ${response.status}`);
      }

      const result = await response.json();
      console.log(`✅ [CLONE] Clone successful:`, result);

      showNotification({
        type: 'success',
        title: t('videoEditor.aspectRatioSplit.notifications.cloneSuccess'),
        message: t('videoEditor.aspectRatioSplit.notifications.cloneSuccessMessage', { 
          ratio: targetRatio, 
          title: result.project.title 
        }),
      });

      if (onCloneSuccess) {
        onCloneSuccess(result.project.id, result.project.title, targetRatio);
      }

      setShowCloneModal(false);
    } catch (error) {
      console.error('❌ [CLONE] Clone error:', error);
      showNotification({
        type: 'error',
        title: t('videoEditor.aspectRatioSplit.notifications.cloneFailed'),
        message: error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setIsCloning(false);
    }
  };

  // Button styles
  const mainButtonStyle: React.CSSProperties = {
    flex: 1,
    background: isConversionMode
      ? 'linear-gradient(135deg, #ef4444, #dc2626)' // Red when active
      : 'linear-gradient(135deg, #6366f1, #8b5cf6)', // Purple when inactive
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '8px 0 0 8px', // Only round left corners
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    padding: '0.75rem 1rem',
    height: '100%'
  };

  const dropdownButtonStyle: React.CSSProperties = {
    background: isConversionMode
      ? 'linear-gradient(135deg, #ef4444, #dc2626)'
      : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white',
    border: 'none',
    borderLeft: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '0 8px 8px 0', // Only round right corners
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    padding: '0.75rem 0.75rem',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <>
      <div 
        ref={dropdownRef}
        style={{
          display: 'flex',
          width: '100%',
          marginTop: '0.5rem',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          position: 'relative'
        }}
      >
        {/* Main Button */}
        <button
          onClick={handleMainButtonClick}
          disabled={disabled}
          style={mainButtonStyle}
        >
          {isConversionMode ? (
            <>{t('videoEditor.aspectRatioSplit.cancelConversion')}</>
          ) : (
            <>{t('videoEditor.aspectRatioSplit.use', { ratio: targetRatio, label: getRatioLabel(targetRatio) })}</>
          )}
        </button>

        {/* Dropdown Arrow Button */}
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled || !projectId}
          style={dropdownButtonStyle}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </button>

        {/* Dropdown Menu */}
        {showDropdown && !disabled && projectId && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            marginTop: '4px',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'dropdownSlideIn 0.2s ease'
          }}>
            {/* Dropdown Item */}
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowCloneModal(true);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <FontAwesomeIcon icon={faClone} style={{ color: '#8b5cf6' }} />
              <div>
                <div style={{ fontWeight: '600' }}>{t('videoEditor.aspectRatioSplit.dropdown.cloneTitle')}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {t('videoEditor.aspectRatioSplit.dropdown.cloneDescription', { ratio: targetRatio })}
                </div>
              </div>
            </button>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes dropdownSlideIn {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      {/* Clone Modal */}
      {showCloneModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: '#2a2a2a',
            padding: '30px',
            borderRadius: '12px',
            width: '500px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            color: 'white'
          }}>
            {/* Header */}
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '600', color: '#8b5cf6' }}>
              <FontAwesomeIcon icon={faClone} /> {t('videoEditor.aspectRatioSplit.modal.title', { ratio: targetRatio })}
            </h2>

            {/* Description */}
            <p style={{ fontSize: '14px', color: '#999', marginBottom: '20px', lineHeight: '1.5' }}>
              {t('videoEditor.aspectRatioSplit.modal.description', { label: getRatioLabel(targetRatio), projectTitle })}
            </p>

            {/* Current Ratio */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                {t('videoEditor.aspectRatioSplit.modal.currentRatio')}
              </label>
              <div style={{ fontSize: '14px', color: '#6366f1' }}>
                {currentAspectRatio} ({getRatioLabel(currentAspectRatio)})
              </div>
            </div>

            {/* Target Ratio */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                {t('videoEditor.aspectRatioSplit.modal.cloneTo')}
              </label>
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#8b5cf6',
                color: 'white',
                border: '1px solid #8b5cf6',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {targetRatio} ({getRatioLabel(targetRatio)})
              </div>
            </div>

            {/* Info Note */}
            <div style={{
              padding: '12px',
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              marginBottom: '20px'
            }}>
              <p style={{ fontSize: '12px', color: '#999', margin: 0, fontStyle: 'italic' }}>
                {t('videoEditor.aspectRatioSplit.modal.infoNote')}
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCloneModal(false)}
                disabled={isCloning}
                style={{
                  padding: '12px 24px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  backgroundColor: 'transparent',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: isCloning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isCloning ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                {t('videoEditor.aspectRatioSplit.modal.cancel')}
              </button>
              <button
                onClick={handleClone}
                disabled={isCloning}
                style={{
                  padding: '12px 24px',
                  background: isCloning ? '#666' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isCloning ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {isCloning && <FontAwesomeIcon icon={faSpinner} spin />}
                {isCloning ? t('videoEditor.aspectRatioSplit.modal.cloning') : t('videoEditor.aspectRatioSplit.modal.cloneButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AspectRatioSplitButton;